import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	ArchiveOutline,
	Box,
	ChatAltOutline,
	ClappingHandsOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PlusSolid,
	Portal,
	rem,
	SmileyFaceOutline,
	Stack,
	Text,
	TrophyOutline,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { FeatureFlag, Permission, ProtectedContent } from '@bonusly/who-am-i';

import { useAwardData } from '../../modules/bonus-feed/features/recognition/components/Awards/hooks/useAwardData';
import { useAwardModal } from '../../modules/bonus-feed/features/recognition/components/Awards/hooks/useAwardModal';
import { useCheckInModal } from '../../modules/check-ins/hooks/use-checkin-modal';
import { useNominationModal } from '../../modules/nominations/hooks';
import { useNominationsCount } from '../../queries/nominations';
import { Reveal } from '../display/reveal';
import { GiveBoxEvent } from '../recognition/give-box/events';
import { NAV_ITEM_WIDTH } from './global-mobile-bottom-nav';

interface CreateActionMenuProps {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
}

export const CreateActionMenu: FC<CreateActionMenuProps> = ({ isOpen, onOpen, onClose }) => {
	const { t } = useTranslation('navigation');
	const dispatch = useDispatch();

	const { onOpen: handleOpenCheckInModal } = useCheckInModal();
	const { onOpen: handleAwardModal } = useAwardModal();
	const { onOpen: handleNominateModal } = useNominationModal();
	const hasAwardsToGive = useAwardData().shouldDisplayBanner;
	const { open: nominationsOpen } = useNominationsCount();

	const handleGiveRecognition = () => {
		onClose();
		dispatch(GiveBoxEvent.Open);
	};

	const handleCheckIn = () => {
		onClose();
		handleOpenCheckInModal();
	};

	const handleAskForAdvice = () => {
		onClose();
		dispatch(FeedbackEvent.OpenRequest);
	};

	const handleAward = () => {
		onClose();
		handleAwardModal();
	};

	const handleNominate = () => {
		onClose();
		handleNominateModal();
	};

	const menuItemStyles = {
		bg: 'bg.surface',
		px: rem(12),
		py: rem(10),
		borderRadius: 'md',
		_hover: { bg: 'secondary.bgAlpha' },
		_focus: { bg: 'secondary.bgAlpha' },
	};

	return (
		<Menu placement="top" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
			<MenuButton
				as={Stack}
				gap={rem(4)}
				aria-label={t('mobileBottomNav.create')}
				alignItems="center"
				textAlign="center"
				w={rem(NAV_ITEM_WIDTH)}
				sx={{
					'&[aria-expanded="true"] .create-icon': {
						transform: 'rotate(52deg)',
					},
					'&:active .create-button-box': {
						transform: 'rotate(-7deg) scale(0.92)',
					},
				}}
				mt={rem(-24)}
			>
				<Box
					className="create-button-box"
					display="flex"
					alignItems="center"
					justifyContent="center"
					w={rem(50)}
					h={rem(50)}
					borderRadius="lg"
					bg="primary.bg"
					transform="rotate(-7deg)"
					mb={rem(10)}
					transition="transform 0.1s ease-in-out"
				>
					<Box
						className="create-icon"
						transition="transform 0.2s ease-in-out"
						transformOrigin="center"
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<PlusSolid size="lg" color="bg.surface" />
					</Box>
				</Box>
				<Text fontSize="xs" color="secondary.fg" fontWeight="semibold">
					{t('mobileBottomNav.create')}
				</Text>
			</MenuButton>

			<Portal>
				<MenuList
					py={rem(8)}
					px={rem(8)}
					bgColor="bg.surface"
					borderRadius="lg"
					borderColor="border.default"
					borderWidth="1px"
					shadow="lg"
					minW={rem(220)}
					zIndex="popover"
				>
					<VStack spacing={rem(2)} align="stretch">
						{/* GIVE RECOGNITION */}
						<ProtectedContent permissions={Permission.CanGiveRecognition}>
							<MenuItem onClick={handleGiveRecognition} {...menuItemStyles}>
								<Box display="flex" alignItems="center" gap={rem(12)}>
									<ClappingHandsOutline size="md" />
									<Text fontSize={100} fontWeight="medium">
										{t('mobileBottomNav.createMenu.giveRecognition')}
									</Text>
								</Box>
							</MenuItem>
						</ProtectedContent>

						{/* CHECK IN */}
						<FeatureFlag flag="manager-enablement-check-in">
							<ProtectedContent permissions={Permission.CanAccessCheckIn}>
								<MenuItem onClick={handleCheckIn} {...menuItemStyles}>
									<Box display="flex" alignItems="center" gap={rem(12)}>
										<SmileyFaceOutline size="md" />
										<Text fontSize={100} fontWeight="medium">
											{t('mobileBottomNav.createMenu.checkIn')}
										</Text>
									</Box>
								</MenuItem>
							</ProtectedContent>
						</FeatureFlag>

						{/* ASK FOR ADVICE (FEEDBACK) */}
						<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
							<MenuItem onClick={handleAskForAdvice} {...menuItemStyles}>
								<Box display="flex" alignItems="center" gap={rem(12)}>
									<ChatAltOutline size="md" />
									<Text fontSize={100} fontWeight="medium">
										{t('mobileBottomNav.createMenu.askForAdvice')}
									</Text>
								</Box>
							</MenuItem>
						</ProtectedContent>

						{/* TODO: Add goals - not global modal yet - https://bonusly1612893911.atlassian.net/browse/SHARK-3611 */}

						{/* AWARD */}
						<ProtectedContent permissions={Permission.CanAccessAwards}>
							<Reveal if={hasAwardsToGive}>
								<MenuItem onClick={handleAward} {...menuItemStyles}>
									<Box display="flex" alignItems="center" gap={rem(12)}>
										<TrophyOutline size="md" />
										<Text fontSize={100} fontWeight="medium">
											{t('mobileBottomNav.createMenu.award')}
										</Text>
									</Box>
								</MenuItem>
							</Reveal>
						</ProtectedContent>

						{/* NOMINATE A TEAMMATE */}
						<ProtectedContent permissions={Permission.CanAccessNominations}>
							<Reveal if={nominationsOpen > 0}>
								<MenuItem onClick={handleNominate} {...menuItemStyles}>
									<Box display="flex" alignItems="center" gap={rem(12)}>
										<ArchiveOutline size="md" />
										<Text fontSize={100} fontWeight="medium">
											{t('mobileBottomNav.createMenu.nominateTeammate')}
										</Text>
									</Box>
								</MenuItem>
							</Reveal>
						</ProtectedContent>

						{/* TODO: Add make announcement - not global modal yet - https://bonusly1612893911.atlassian.net/browse/SHARK-3611 */}
					</VStack>
				</MenuList>
			</Portal>
		</Menu>
	);
};
